/**
 * Foundation Data v2 entity selection: which entities the list page offers, which one it opens on, and which
 * years an entity is missing.
 *
 * A workstream belongs to one entity (an `IssbEntity`, Settings → Entities), so a year holds three workstreams
 * PER ENTITY. The list page shows one entity at a time.
 *
 * A standalone file because the `src/utils` barrel contains JSX and breaks vitest imports.
 */

/**
 * The entities to offer, by name: every entity that holds a workstream, plus every active organisation entity.
 *
 * Both sources, because neither is complete on its own. The organisation list omits ARCHIVED entities, whose
 * figures are still there and still worth reading; the workstream rows omit an entity created since the last
 * year was added, which is exactly the one somebody needs to add to that year.
 *
 * @param {Array<{entityId: string, entityName?: string}>} years the rows of `GET /foundation-data-v2/years`
 * @param {Array<{id: string, name?: string}>} orgEntities the rows of `GET /issb/entity`
 * @returns {Array<{id: string, name: string}>}
 */
export function entityOptions(years, orgEntities) {
  const byId = new Map();
  (years || []).forEach((y) => {
    if (y?.entityId && !byId.has(y.entityId)) {
      byId.set(y.entityId, { id: y.entityId, name: y.entityName || "Unnamed entity" });
    }
  });
  (orgEntities || []).forEach((e) => {
    if (e?.id && !byId.has(e.id)) byId.set(e.id, { id: e.id, name: e.name || "Unnamed entity" });
  });
  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
}

/**
 * The entity the page opens on: the one asked for when it is still offered, else the first offered that holds
 * any workstream, else the first offered at all.
 *
 * Preferring one with workstreams means an organisation that has just created an entity in Settings does not
 * land on an empty page while its existing figures sit one click away.
 */
export function chooseEntity(options, requestedId, years) {
  if (!options?.length) return null;
  if (requestedId && options.some((o) => o.id === requestedId)) return requestedId;
  const held = new Set((years || []).map((y) => y.entityId));
  return (options.find((o) => held.has(o.id)) ?? options[0]).id;
}

/**
 * The years the organisation collects that this entity holds no workstream for, newest first — what
 * "Add <entity> to <year>" offers.
 */
export function yearsMissingForEntity(years, entityId) {
  if (!entityId) return [];
  const all = new Set();
  const held = new Set();
  (years || []).forEach((y) => {
    all.add(y.year);
    if (y.entityId === entityId) held.add(y.year);
  });
  return [...all].filter((year) => !held.has(year)).sort((a, b) => b - a);
}

/** Whether a question (an `IssbQuestionDto`) belongs to an entity — the list page filters on this. */
export function questionBelongsToEntity(question, entityId) {
  return !!entityId && (question?.entities || []).includes(entityId);
}

/**
 * Whether a figure is in scope for an AIR round (ESG-4019): the round's own entities, or EVERY entity when the
 * round names none. The same rule Java's `listCuratedRowsForAgent` applies to what AI Review drafts from — keep
 * the two in step, or the answer editor offers figures the AI never sees (or hides ones it does).
 *
 * @param {{entities?: string[]}|null|undefined} round
 * @param {string|null|undefined} entityId the figure's (or workstream row's) entity
 */
export function inRoundEntities(round, entityId) {
  const ids = (round?.entities ?? []).filter(Boolean);
  return ids.length === 0 || ids.includes(entityId);
}

/** Whether a round is scoped to particular entities at all. */
export function roundHasEntities(round) {
  return (round?.entities ?? []).some(Boolean);
}
